import { useEffect, useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkles, Loader2, CheckCircle2, Circle, Timer, Copy, RefreshCw, Rocket } from "lucide-react";
import { cn } from "@/lib/utils";
import RevenueImpact from "@/components/RevenueImpact";
import ImpactCard from "@/components/ImpactCard";

// SHA-1 first 16 hex chars — must match backend _rec_key
async function recKey(title, step) {
  const raw = `${(title || "").trim().toLowerCase()}|${(step || "").trim().toLowerCase()}`;
  const buf = new TextEncoder().encode(raw);
  const hash = await crypto.subtle.digest("SHA-1", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 16);
}

const STATUS_META = (t) => ({
  todo: { label: t("cro_status_todo"), icon: Circle, cls: "text-muted-foreground" },
  in_progress: { label: t("cro_status_progress"), icon: Timer, cls: "text-[hsl(var(--chart-1))]" },
  done: { label: t("cro_status_done"), icon: CheckCircle2, cls: "text-[hsl(var(--chart-3))]" },
});

// Build a ready-to-paste Emergent prompt from a CRO recommendation.
function buildEmergentPrompt(rec, shopDomain) {
  const site = shopDomain ? `https://${shopDomain}` : "unser Shop";
  const uplift = rec.expected_uplift_pct ? `${rec.expected_uplift_pct}% mehr Conversion` : "eine höhere Conversion";
  const revenue = rec.estimated_revenue_eur
    ? ` (~${new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(rec.estimated_revenue_eur)} zusätzlicher Umsatz)`
    : "";
  return `Emergent-Auftrag für ${site}

Ziel: ${rec.title}
Erwartete Wirkung: ${uplift}${revenue}
Betroffener Funnel-Schritt: ${rec.step}

## Problem
${rec.problem || "-"}

## Hypothese
${rec.hypothesis || "-"}

## Konkrete Umsetzung
${rec.experiment || "-"}

## Was du (Emergent) machen sollst
1. Analysiere die aktuelle Umsetzung dieses Punkts auf ${site}. Zeige mir die betroffenen Templates/Komponenten/Seiten mit Datei-Pfaden.
2. Implementiere die Änderung EXAKT wie oben beschrieben. Wenn du Vermutungen triffst (z. B. bezüglich Text, Layout oder Farben), markiere sie klar als "Annahme".
3. Halte den bestehenden Design-Sprache/Tone-of-Voice ein und nutze die bestehende Design-System-Komponenten und Farbvariablen.
4. Baue sinnvolle, echte Deutsche + Englische Texte ein (kein Lorem Ipsum, keine Platzhalter).
5. Falls A/B-Testing im Shop technisch möglich ist, schlage einen sauberen A/B-Split vor (Variante A = Status quo, Variante B = deine neue Implementierung). Sonst als 100% Rollout mit einer klaren "Rollback"-Anleitung.
6. Zeige mir am Ende: (a) alle geänderten Dateien mit Diff, (b) eine kurze QA-Checkliste, (c) wie ich die Umsatzwirkung im GA4/Shop-Analytics messe.

Wichtig: Nichts anderes ändern als das Nötige. Keine Refactorings, keine "kleinen Verbesserungen" nebenbei.`;
}

export default function CroActionControls({ shopId, shopDomain, rec, actions }) {
  const { t } = useLang();
  const qc = useQueryClient();
  const [key, setKey] = useState(null);
  const [planOpen, setPlanOpen] = useState(false);
  const [pollTaskId, setPollTaskId] = useState(null);

  useEffect(() => {
    let ok = true;
    recKey(rec.title, rec.step).then((k) => ok && setKey(k));
    return () => {
      ok = false;
    };
  }, [rec.title, rec.step]);

  const action = useMemo(
    () => actions?.find((a) => a.rec_key === key),
    [actions, key],
  );

  const status = action?.status || "todo";
  const meta = STATUS_META(t);
  const currentMeta = meta[status];
  const StatusIcon = currentMeta.icon;

  const statusMutation = useMutation({
    mutationFn: async (newStatus) =>
      (await api.put(`/shops/${shopId}/cro/actions/${key}`, { status: newStatus })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cro-actions", shopId] });
    },
    onError: () => toast.error(t("cro_status_error")),
  });

  const startPlan = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/shops/${shopId}/cro/plan`, {
          title: rec.title,
          step: rec.step,
          problem: rec.problem,
          hypothesis: rec.hypothesis,
          experiment: rec.experiment,
          priority: rec.priority,
          effort: rec.effort,
        })
      ).data,
    onSuccess: (data) => {
      setPollTaskId(data.task_id);
      setPlanOpen(true);
      toast.info(t("cro_plan_generating"));
    },
    onError: () => toast.error(t("cro_plan_error")),
  });

  // Poll for plan task completion
  const { data: planTask } = useQuery({
    queryKey: ["cro-plan-task", pollTaskId],
    queryFn: async () => (await api.get(`/cro/plan-tasks/${pollTaskId}`)).data,
    enabled: !!pollTaskId,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === "done" || s === "failed" ? false : 2000;
    },
  });

  useEffect(() => {
    if (planTask?.status === "done") {
      qc.invalidateQueries({ queryKey: ["cro-actions", shopId] });
      setPollTaskId(null);
    }
  }, [planTask?.status, qc, shopId]);

  const isPlanLoading = startPlan.isPending || (pollTaskId && planTask?.status !== "done");
  const planMarkdown = planTask?.plan_markdown || action?.plan_markdown;
  const hasPlan = !!planMarkdown;

  const cycleStatus = () => {
    const next = status === "todo" ? "in_progress" : status === "in_progress" ? "done" : "todo";
    statusMutation.mutate(next);
  };

  return (
    <>
      <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-2 items-center">
        <button
          type="button"
          onClick={cycleStatus}
          disabled={!key || statusMutation.isPending}
          data-testid={`cro-status-toggle-${key || "loading"}`}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-mono uppercase tracking-widest border transition-colors",
            status === "done"
              ? "border-[hsl(var(--chart-3))]/40 bg-[hsl(var(--chart-3))]/10"
              : status === "in_progress"
              ? "border-[hsl(var(--chart-1))]/40 bg-[hsl(var(--chart-1))]/10"
              : "border-border bg-muted hover:bg-muted/70",
            currentMeta.cls,
          )}
        >
          <StatusIcon className="h-3 w-3" />
          {currentMeta.label}
        </button>

        <div className="ml-auto flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              navigator.clipboard.writeText(buildEmergentPrompt(rec, shopDomain));
              toast.success(t("cro_emergent_prompt_copied"));
            }}
            data-testid={`cro-copy-emergent-${key || "loading"}`}
            className="gap-1.5"
            title={t("cro_copy_emergent_prompt")}
          >
            <Rocket className="h-3.5 w-3.5" />
            {t("cro_copy_emergent_prompt_short")}
          </Button>
          {hasPlan && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPlanOpen(true)}
              data-testid={`cro-view-plan-${key}`}
            >
              {t("cro_view_plan")}
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => startPlan.mutate()}
            disabled={!key || isPlanLoading}
            data-testid={`cro-generate-plan-${key || "loading"}`}
            className="gap-1.5"
          >
            {isPlanLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : hasPlan ? (
              <RefreshCw className="h-3.5 w-3.5" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            {isPlanLoading
              ? t("cro_plan_generating_short")
              : hasPlan
              ? t("cro_plan_regenerate")
              : t("cro_generate_plan")}
          </Button>
        </div>
      </div>

      {key && <ImpactCard shopId={shopId} recKey={key} status={status} />}

      <PlanDialog
        open={planOpen}
        onOpenChange={setPlanOpen}
        title={rec.title}
        markdown={planMarkdown}
        loading={pollTaskId && planTask?.status !== "done"}
        shopId={shopId}
        shopDomain={shopDomain}
        rec={rec}
        upliftPct={rec.expected_uplift_pct}
        onMarkDone={() => {
          statusMutation.mutate("done");
          setPlanOpen(false);
        }}
      />
    </>
  );
}

// Wrap the FULL step-by-step plan markdown into a ready-to-paste Emergent prompt.
function buildEmergentPlanPrompt(rec, shopDomain, planMarkdown) {
  const site = shopDomain ? `https://${shopDomain}` : "unser Shop";
  const uplift = rec?.expected_uplift_pct ? `${rec.expected_uplift_pct}% mehr Conversion` : "eine höhere Conversion";
  const revenue = rec?.estimated_revenue_eur
    ? ` (~${new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(rec.estimated_revenue_eur)} zusätzlicher Umsatz/Jahr)`
    : "";
  return `Emergent-Auftrag für ${site}

Ziel: ${rec?.title || "CRO-Optimierung"}
Erwartete Wirkung: ${uplift}${revenue}
Betroffener Funnel-Schritt: ${rec?.step || "-"}

Unten findest du den KOMPLETTEN Umsetzungsplan mit allen Schritten, Code-Snippets und Content-Vorlagen.
Bitte arbeite jeden Schritt der Reihe nach ab.

## Was du (Emergent) machen sollst
1. Lies den Plan komplett, bevor du anfängst zu coden.
2. Arbeite die Schritte in der angegebenen Reihenfolge ab. Kommentiere im PR nach jedem Schritt, was du geändert hast (Datei + kurze Zusammenfassung).
3. Nutze die bereitgestellten Texte, Copy-Bausteine und Code-Snippets EINS ZU EINS — nicht "verbessern" oder umformulieren. Wenn ein Text angepasst werden muss (z. B. auf Sprache/Marken-Ton von ${site}), markiere das als "Annahme".
4. Halte dich strikt an das bestehende Design-System (Farbvariablen, Typography, Komponenten). Keine neuen UI-Bibliotheken einführen.
5. Baue echte, verkaufsstarke deutsche + englische Texte ein. Kein Lorem Ipsum, keine Platzhalter.
6. Nichts anderes ändern als das Nötige — keine Refactorings, keine "kleinen Verbesserungen" nebenbei.
7. Am Ende zeige mir: (a) Liste aller geänderten Dateien mit Diff-Zusammenfassung, (b) eine kurze QA-Checkliste (was ich manuell testen soll), (c) wie ich die Umsatzwirkung in GA4 / Shop-Analytics messe (welche Events, welche Segmente), (d) klare Rollback-Anleitung falls etwas schiefgeht.

---

# Umsetzungsplan (von Rankly AI erstellt)

${planMarkdown || "(kein Plan vorhanden)"}`;
}

const MD_COMPONENTS = {
  pre: ({ children }) => <CopyablePre>{children}</CopyablePre>,
};

function PlanDialog({ open, onOpenChange, title, markdown, loading, onMarkDone, shopId, shopDomain, rec, upliftPct }) {
  const { t } = useLang();
  const copyAll = () => {
    if (!markdown) return;
    navigator.clipboard.writeText(markdown);
    toast.success(t("cro_plan_copied"));
  };
  const copyEmergentPrompt = () => {
    if (!markdown) return;
    navigator.clipboard.writeText(buildEmergentPlanPrompt(rec, shopDomain, markdown));
    toast.success(t("cro_emergent_prompt_copied"));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-base">{title}</DialogTitle>
          <DialogDescription className="text-xs font-mono uppercase tracking-widest">
            {t("cro_plan_title")}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto pr-2 flex-1 -mr-2">
          {upliftPct && shopId && (
            <RevenueImpact shopId={shopId} upliftPct={upliftPct} variant="banner" />
          )}
          {loading && !markdown ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-sm text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
              {t("cro_plan_generating_long")}
            </div>
          ) : markdown ? (
            <div className="prose prose-sm dark:prose-invert max-w-none prose-headings:font-display prose-h2:text-base prose-h2:mt-6 prose-h2:mb-2 prose-h2:border-b prose-h2:pb-1 prose-code:text-xs prose-pre:text-xs prose-pre:bg-muted prose-pre:text-foreground prose-pre:border prose-pre:border-border">
              <ReactMarkdown components={MD_COMPONENTS}>
                {markdown}
              </ReactMarkdown>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground py-8 text-center">
              {t("cro_plan_none")}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 flex-shrink-0 pt-4 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={copyAll}
            disabled={!markdown}
            data-testid="cro-plan-copy-all"
            className="gap-1.5"
          >
            <Copy className="h-3.5 w-3.5" />
            {t("cro_plan_copy_all")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={copyEmergentPrompt}
            disabled={!markdown}
            data-testid="cro-plan-copy-emergent"
            className="gap-1.5"
          >
            <Rocket className="h-3.5 w-3.5" />
            {t("cro_copy_emergent_prompt")}
          </Button>
          <Button
            size="sm"
            onClick={onMarkDone}
            disabled={!markdown}
            data-testid="cro-plan-mark-done"
            className="gap-1.5"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            {t("cro_plan_mark_done")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CopyablePre({ children }) {
  const [copied, setCopied] = useState(false);
  // children is <code>...</code> node
  const codeText =
    (children?.props?.children ?? "")
      .toString();
  const copy = () => {
    navigator.clipboard.writeText(codeText);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="relative group">
      <pre>{children}</pre>
      <button
        type="button"
        onClick={copy}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-background border border-border rounded px-2 py-1 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1"
      >
        {copied ? (
          <>
            <CheckCircle2 className="h-3 w-3 text-[hsl(var(--chart-3))]" />
            copied
          </>
        ) : (
          <>
            <Copy className="h-3 w-3" />
            copy
          </>
        )}
      </button>
    </div>
  );
}
